import { useContext, useState } from "react";
import InvoiceService from "../../../services/InvoiceService";
import Collapsable from "../../shared/bootstrap/Collapsable";
import ModalService, { ModalParams } from "../../shared/bootstrap/modal/Modal";
import ToastContext, { ToastService } from "../../shared/bootstrap/Toast";
import { TranslationService } from "../../../services/TranslationService";
import TableContext from "../../task/TableContext";
import { InvoiceEditStatus } from "./entities/InvoiceEditStatus";
import { InvoiceMarkAsPayed } from "./InvoiceMarkAsPayed";
import { InvoiceChangeStatusForm } from "./InvoiceChangeStatusForm";
import { InvoiceMassiveEditFields } from "./InvoiceMassiveEditFields";
import { formatCurrency } from "../../../utils/FormatUtils";
import { InvoiceTableContextValues } from "./InvoiceTableProvider";
import ClientDetailContext, {
  ClientDetailContextValues
} from "../ClientDetailContext";
import TooltipComponent from "../../shared/TooltipComponent";
import FloatingPanelService from "../../shared/FloatingPanel";
import CompanyService from "../../../services/CompanyService";
import CurrencyService from "../../../services/CurrencyService";

const InvoiceMassiveChanges = ({
  personId,
  showSelectedOnly,
  setShowSelectedOnly
}: {
  personId: number;
  showSelectedOnly?: boolean;
  setShowSelectedOnly?: (value: boolean) => void;
}) => {
  const { translate } = TranslationService;
  const clientDetailContext = useContext(ClientDetailContext);
  const { invoiceIds } = clientDetailContext;
  const { showModal, hideModal } = ModalService;
  const { showToast } = useContext(ToastContext);

  const { reload } = useContext(TableContext) as InvoiceTableContextValues;
  const selectedCount = invoiceIds.length;
  const getCurrencyTotals = () => {
    return getSelectedCurrencyTotals().map((x) =>
      formatCurrency(x.pendingAmount, x.currencyId)
    );
  };
  const getSelectedCurrencyTotals = () => {
    const pendingAmounts: { currencyId: number; pendingAmount: number }[] = [];
    const defaultCurrencyId = CompanyService.getDefaultCurrencyId();

    // Usar datos almacenados en lugar de response.list
    invoiceIds.forEach((id) => {
      const invoiceData = clientDetailContext.selectedInvoiceData.get(id);
      if (!invoiceData) {
        return;
      }

      const pendingAmount = invoiceData.pending;
      const currencyId = invoiceData.CurrencyID;
      if (CompanyService.getSetting("groupediototals")) {
        const currencySum = pendingAmounts.find(
          (x) => x.currencyId === currencyId
        );
        if (currencySum) {
          currencySum.pendingAmount += pendingAmount;
        } else {
          pendingAmounts.push({ currencyId, pendingAmount });
        }
      } else {
        const currencySum = pendingAmounts.find(
          (x) => x.currencyId === defaultCurrencyId
        );
        const toSum = CurrencyService.convert(
          pendingAmount,
          currencyId,
          defaultCurrencyId
        );
        if (currencySum) {
          currencySum.pendingAmount += toSum;
        } else {
          pendingAmounts.push({
            currencyId: defaultCurrencyId,
            pendingAmount: toSum
          });
        }
      }
    });

    return pendingAmounts;
  };
  //No pay modal
  const onModalSubmit = () => {
    hideModal();
    reload();
  };

  const noPayModal = new ModalParams();
  noPayModal.children = (
    <ConfirmNoPay
      personId={personId}
      onSubmit={onModalSubmit}
      selectedCount={selectedCount}
      clientDetailContext={clientDetailContext}
    />
  );
  noPayModal.closeClickingOutside = true;
  noPayModal.title = translate.MarkAsNotPaid;

  //Edit multiple modal
  const editMultipleInvoices = new ModalParams();
  editMultipleInvoices.children = (
    <InvoiceMassiveEditFields
      onSubmit={onModalSubmit}
      personId={personId.toString()}
      clientDetailContext={clientDetailContext}
    />
  );
  editMultipleInvoices.closeClickingOutside = true;
  editMultipleInvoices.title = translate.MultiInvoiceEdit;
  editMultipleInvoices.size = "modal-lg";

  const showMarkAsPaid = () => {
    FloatingPanelService.showPanel({
      title: translate.MarkAsPaid,
      children: (
        <InvoiceMarkAsPayed
          personId={personId}
          onSubmit={onModalSubmit}
          selectedInvoiceCount={selectedCount}
          clientDetailContext={clientDetailContext}
        />
      ),
      width: 800,
      height: 669
    });
  };

  //Change status modal
  const onStatusSubmit = async (statusEdit: InvoiceEditStatus) => {
    const result = await InvoiceService.statusChange({
      invoiceIds: invoiceIds,
      personId: personId.toString(),
      statusEdit
    });
    if (result instanceof Error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
      hideModal();
    } else {
      showToast(translate.SavedChanges, undefined, "success");
      if (result.refreshactivity) {
        clientDetailContext.reloadExtraData();
      }
      // Limpiar todas las selecciones después del cambio masivo de estado
      clientDetailContext.setInvoiceIds([]);
      clientDetailContext.setSelectedInvoiceData(new Map());
      // Si estaba activo "mostrar solo seleccionadas", desactivarlo después de limpiar
      // El cambio de showSelectedOnly hará que selectedInvoiceIds cambie a undefined,
      // lo que disparará automáticamente el useEffect en InvoiceTableProvider para recargar
      // Por lo tanto, no necesitamos llamar a reload() explícitamente en este caso
      if (showSelectedOnly && setShowSelectedOnly) {
        setShowSelectedOnly(false);
        // No llamar a reload() porque el cambio de selectedInvoiceIds ya lo hará automáticamente
      } else {
        // Solo llamar a reload() si no había showSelectedOnly activo
        reload();
      }
      hideModal();
    }
  };

  const TotalsCurrency = getCurrencyTotals().map((currency) => (
    <span key={currency}>{currency}</span>
  ));
  const changeStatusModal = new ModalParams();
  changeStatusModal.children = (
    <InvoiceChangeStatusForm
      onSubmit={onStatusSubmit}
      personId={personId.toString()}
    />
  );
  changeStatusModal.closeClickingOutside = true;
  changeStatusModal.title = translate.ChangeIOStatus;

  return (
    <Collapsable show={selectedCount > 0}>
      <div
        data-bs-toggle="collapse"
        className="selectedCollapse pb-3"
        style={{ width: "fit-content" }}
      >
        <div className="quantity me-2">
          <p>
            {translate.NumberSelectedInvoices.replace(
              "{invoiceCount}",
              selectedCount.toString()
            )}
          </p>
          {TotalsCurrency}
        </div>
        {CompanyService.canDo("changeiostatus") && (
          <button
            className="btn btn-primary"
            onClick={() => showModal(changeStatusModal)}
          >
            {" "}
            {translate.ChangeIOStatus}
          </button>
        )}
        <div className="actions">
          {setShowSelectedOnly && selectedCount > 0 && (
            <TooltipComponent
              key={`eye-tooltip-${showSelectedOnly}`}
              title={
                showSelectedOnly
                  ? translate.ShowAll
                  : translate.ShowSelectedInvoices
              }
            >
              <button
                type="button"
                className="btn btn-link p-0 border-0"
                onClick={() => setShowSelectedOnly(!showSelectedOnly)}
                style={{
                  color: showSelectedOnly ? "#6c757d" : "#0076b6",
                  fontSize: "1.2rem"
                }}
              >
                <i
                  className={
                    showSelectedOnly
                      ? "fa-regular fa-eye-slash"
                      : "fa-regular fa-eye"
                  }
                />
              </button>
            </TooltipComponent>
          )}
          {/*  */}
          {!CompanyService.getSetting("hideeditio") &&
            CompanyService.canDo("editio") && (
              <>
                <TooltipComponent title={translate.EditValues}>
                  <button onClick={() => showModal(editMultipleInvoices)}>
                    <i className="far fa-edit"></i>
                  </button>
                </TooltipComponent>
              </>
            )}
          {!CompanyService.getSetting("hideeditpayment") &&
            CompanyService.canDo("newtransaction") && (
              <TooltipComponent title={translate.MarkAsPaid}>
                <button onClick={() => showMarkAsPaid()}>
                  <i className="far fa-check-circle"></i>
                </button>
              </TooltipComponent>
            )}
          {!CompanyService.getSetting("hideeditpayment") &&
            CompanyService.canDo("deletetransaction") && (
              <TooltipComponent title={translate.MarkAsNotPaid}>
                <button onClick={() => showModal(noPayModal)}>
                  <i className="far fa-times-circle"></i>
                </button>
              </TooltipComponent>
            )}
        </div>
        {/* <FilterDropdownButton items={items} title={translate.MassiveActions} /> */}
      </div>
    </Collapsable>
  );
};

const ConfirmNoPay = ({
  personId,
  onSubmit,
  selectedCount,
  clientDetailContext
}: {
  personId: number;
  onSubmit: () => void;
  selectedCount: number;
  clientDetailContext: ClientDetailContextValues;
}) => {
  const { translate } = TranslationService;
  const { invoiceIds, setInvoiceIds, setSelectedInvoiceData } =
    clientDetailContext;
  const [isSaving, setIsSaving] = useState(false);

  const confirmNoPay = async () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    const result = await InvoiceService.togglePay({
      pay: false,
      invoiceIds: invoiceIds,
      personId: personId.toString(),
      additional: [],
      receipt: false
    });
    if (result instanceof Error) {
      ToastService.showToast(
        translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
    } else {
      ToastService.showToast(translate.SavedChanges, undefined, "success");
      setInvoiceIds([]);
      setSelectedInvoiceData(new Map());
      onSubmit && onSubmit();
    }
    setIsSaving(false);
  };

  return (
    <>
      <div className="modal-body">
        <div className="d-flex input-column mb-3">
          <p>
            {(selectedCount === 1
              ? translate.MassiveActionConfirmNoPay
              : translate.MassiveActionConfirmNoPayMultiple
            ).replaceAll("{0}", selectedCount.toString())}
          </p>
        </div>
      </div>
      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => ModalService.hideModal()}
        >
          {translate.Close}
        </button>
        <button className="btn btn-primary" onClick={confirmNoPay}>
          {translate.Confirm}
          {isSaving && (
            <i className="fas fa-spinner-third fa-spin third ms-2"></i>
          )}
        </button>
      </div>
    </>
  );
};

export default InvoiceMassiveChanges;
